import {
  useState,
  useEffect,
  useCallback,
  useRef,
  KeyboardEvent,
  RefObject,
} from "react";
import { FolderTreeItem } from "@zapier/client";

interface UseFolderTreeKeyboardNavigationInput {
  folders: FolderTreeItem[];
  selectedFolderId?: string;
  expandedFolders: Set<string | undefined>;
  onToggleExpanded: (folderId?: string) => void;
  onFolderClick?: (folder: FolderTreeItem) => void;
  treeRef: RefObject<HTMLElement>;
  isRoot?: boolean;
}

interface UseFolderTreeKeyboardNavigationOutput {
  focusableFolderId: string;
  setFocusableFolderId: (folderId: string) => void;
  handleKeyDown: (event: KeyboardEvent, folder: FolderTreeItem) => void;
  getFocusableTabIndex: (folderId: string) => 0 | -1;
}

/**
 * WCAG-compliant keyboard navigation for folder trees using roving tabindex pattern.
 * Implements the W3C Tree View Pattern: https://www.w3.org/WAI/ARIA/apg/patterns/treeview/
 *
 * Key behaviors:
 * - Tab: Focus tree (only one item is tabbable at a time)
 * - Arrow Up/Down: Navigate between items
 * - Arrow Right: Expand folder or move to first child
 * - Arrow Left: Collapse folder or move to parent
 * - Enter/Space: Activate item (navigate or select)
 * - Type-ahead: Jump to item by typing its first letter
 */
export const useFolderTreeKeyboardNavigation = ({
  folders,
  selectedFolderId,
  expandedFolders,
  onToggleExpanded,
  onFolderClick,
  treeRef,
  isRoot = true,
}: UseFolderTreeKeyboardNavigationInput): UseFolderTreeKeyboardNavigationOutput => {
  const [focusableFolderId, setFocusableFolderId] = useState<string>(() => {
    return selectedFolderId || "root";
  });

  // Tracks whether the tree currently owns focus. Used to gate the focus
  // restore effect so it only ever restores focus that was already inside
  // the tree — never steals focus from elsewhere on the page.
  const treeHasFocusReference = useRef(false);

  const getFolderIdFromParentElement = useCallback((element: HTMLElement) => {
    const parentLi = element.parentElement;
    return parentLi?.getAttribute("data-folder-id");
  }, []);

  useEffect(() => {
    if (selectedFolderId) {
      setFocusableFolderId(selectedFolderId);
    }
  }, [selectedFolderId]);

  // Track whether focus is inside the tree via focusin/focusout listeners.
  // The null-relatedTarget carve-out on focusout is critical: when React
  // unmounts the focused element (which happens on every re-render because
  // FolderTreeBranch defines its item renderer inline), the browser fires
  // focusout with relatedTarget === null. If we flipped the flag then, we
  // would silently break focus restoration.
  useEffect(() => {
    const treeElement = treeRef.current;
    if (!treeElement) return;

    const handleFocusIn = () => {
      treeHasFocusReference.current = true;
    };
    const handleFocusOut = (event: FocusEvent) => {
      if (
        event.relatedTarget &&
        !treeElement.contains(event.relatedTarget as Node)
      ) {
        treeHasFocusReference.current = false;
      }
    };

    treeElement.addEventListener("focusin", handleFocusIn);
    treeElement.addEventListener("focusout", handleFocusOut);
    return () => {
      treeElement.removeEventListener("focusin", handleFocusIn);
      treeElement.removeEventListener("focusout", handleFocusOut);
    };
  }, [treeRef]);

  // Restore focus after tree re-renders that unmount the focused row. This
  // happens on expand/collapse and also on arrow-key navigation, because
  // FolderTreeBranch defines its item renderer inline (each render creates
  // a new component type, forcing React to fully unmount all items).
  //
  // The treeHasFocusReference gate discriminates between "the tree lost
  // focus because a re-render unmounted the focused row" (restore it) and
  // "focus is elsewhere on the page" (leave it alone). The gate is what
  // stops external selectedFolderId changes from stealing focus.
  useEffect(() => {
    if (!treeRef.current) return;
    if (!treeHasFocusReference.current) return;

    const treeElement = treeRef.current;
    const hasFocusWithin = treeElement.contains(document.activeElement);

    if (
      !hasFocusWithin &&
      focusableFolderId &&
      document.activeElement?.tagName === "BODY"
    ) {
      const allItems = treeElement.querySelectorAll('[role="treeitem"]');
      const targetItem = [...allItems].find((item) => {
        return (
          getFolderIdFromParentElement(item as HTMLElement) ===
          focusableFolderId
        );
      });

      if (targetItem) {
        (targetItem as HTMLElement).focus({ preventScroll: true });
      }
    }
  }, [
    expandedFolders,
    focusableFolderId,
    treeRef,
    getFolderIdFromParentElement,
  ]);

  // Query all currently rendered tree item elements
  const getAllVisibleTreeItems = useCallback((): HTMLElement[] => {
    if (!treeRef.current) return [];
    const rootTree = isRoot
      ? treeRef.current
      : treeRef.current.closest('[role="tree"]');
    if (!rootTree) return [];

    return [...rootTree.querySelectorAll('[role="treeitem"]')] as HTMLElement[];
  }, [treeRef, isRoot]);

  // Searching can filter away the row holding the tree's single tab stop,
  // which leaves every row at tabIndex -1 and drops the tree out of the tab
  // order entirely. Hand the stop to the first row that is still rendered.
  useEffect(() => {
    const items = getAllVisibleTreeItems();
    if (items.length === 0) return;
    const stillRendered = items.some(
      (item) => getFolderIdFromParentElement(item) === focusableFolderId,
    );
    if (stillRendered) return;
    const firstId = getFolderIdFromParentElement(items[0]);
    if (firstId) setFocusableFolderId(firstId);
  }, [
    folders,
    focusableFolderId,
    getAllVisibleTreeItems,
    getFolderIdFromParentElement,
  ]);

  // Recursively find parent folder in tree data structure
  const findParentFolder = useCallback(
    (
      targetFolder: FolderTreeItem,
      currentFolders: FolderTreeItem[],
      parent?: FolderTreeItem,
    ): FolderTreeItem | undefined => {
      for (const folder of currentFolders) {
        if (folder.id === targetFolder.id && parent) {
          return parent;
        }
        if (folder.children && folder.children.length > 0) {
          const result = findParentFolder(
            targetFolder,
            folder.children as FolderTreeItem[],
            folder,
          );
          if (result) return result;
        }
      }
      return undefined;
    },
    [],
  );

  // Get first child of a folder if expanded
  const getFirstChild = useCallback(
    (folder?: FolderTreeItem): FolderTreeItem | undefined => {
      const isExpanded = expandedFolders.has(String(folder?.id));
      if (isExpanded && folder?.children && folder.children.length > 0) {
        return folder.children[0] as FolderTreeItem;
      }
      return undefined;
    },
    [expandedFolders],
  );

  // Focus a specific folder by ID
  const focusFolder = useCallback(
    (folderId: string) => {
      const allItems = getAllVisibleTreeItems();
      const targetItem = allItems.find((item) => {
        return getFolderIdFromParentElement(item) === folderId;
      });
      if (targetItem && targetItem.tabIndex !== undefined) {
        try {
          targetItem.focus();
          setFocusableFolderId(folderId);
        } catch {
          // Ignore if focus fails (element may be hidden or removed)
        }
      }
    },
    [getAllVisibleTreeItems, getFolderIdFromParentElement],
  );

  // Main keyboard event handler following WCAG Tree View pattern
  const handleKeyDown = useCallback(
    (event: KeyboardEvent, folder: FolderTreeItem) => {
      const { key, target } = event;
      const allItems = getAllVisibleTreeItems();

      // Find the actual treeitem element (in case event fired on a child)
      const currentTreeItem =
        (target as HTMLElement).closest('[role="treeitem"]') ||
        (target as HTMLElement);
      const currentIndex = allItems.indexOf(currentTreeItem as HTMLElement);

      // If we can't find the current item, try to find it by folder id
      const actualCurrentIndex =
        currentIndex === -1
          ? allItems.findIndex((item) => {
              return getFolderIdFromParentElement(item) === String(folder.id);
            })
          : currentIndex;

      const isExpanded = expandedFolders.has(String(folder.id));
      const hasChildren = !!folder.children?.length;

      switch (key) {
        case "Enter":
        case " ": {
          // If it's a link element, trigger click to let Next.js Link handle navigation
          if (currentTreeItem instanceof HTMLAnchorElement) {
            if (key === "Enter") {
              // Let Enter naturally trigger the link
              return;
            } else {
              event.preventDefault(); // Prevent page scroll
              currentTreeItem.click();
              return;
            }
          }

          // For buttons, prevent default and call onClick
          event.preventDefault();
          if (onFolderClick) {
            onFolderClick(folder);
          }
          break;
        }

        case "ArrowDown": {
          event.preventDefault();
          if (
            actualCurrentIndex !== -1 &&
            actualCurrentIndex < allItems.length - 1
          ) {
            const nextItem = allItems[actualCurrentIndex + 1];
            if (nextItem && nextItem.getAttribute("role") === "treeitem") {
              const nextFolderId = getFolderIdFromParentElement(nextItem);
              if (nextFolderId) {
                setFocusableFolderId(nextFolderId);
                nextItem.focus();
              }
            }
          }
          break;
        }

        case "ArrowUp": {
          event.preventDefault();
          if (actualCurrentIndex > 0) {
            const previousItem = allItems[actualCurrentIndex - 1];
            if (
              previousItem &&
              previousItem.getAttribute("role") === "treeitem"
            ) {
              const previousFolderId =
                getFolderIdFromParentElement(previousItem);
              if (previousFolderId) {
                setFocusableFolderId(previousFolderId);
                previousItem.focus();
              }
            }
          }
          break;
        }

        case "ArrowRight": {
          event.preventDefault();
          if (hasChildren) {
            if (isExpanded) {
              // Move to first child
              const firstChild = getFirstChild(folder);
              if (firstChild) {
                focusFolder(firstChild.id);
              }
            } else {
              // Expand folder (focus maintained via useEffect)
              onToggleExpanded(folder.id);
            }
          }
          break;
        }

        case "ArrowLeft": {
          event.preventDefault();
          if (hasChildren && isExpanded) {
            // Close open node - focus maintained automatically through tabIndex
            onToggleExpanded(folder.id);
          } else {
            // Move to parent
            const parent = findParentFolder(folder, folders);
            if (parent) {
              focusFolder(parent.id);
            }
          }
          break;
        }

        default: {
          // Type-ahead: Jump to item starting with typed character
          if (key.length === 1 && /[a-zA-Z]/.test(key)) {
            event.preventDefault();
            const searchChar = key.toLowerCase();
            const startIndex = actualCurrentIndex + 1;

            // Search forward from current position
            for (let index = startIndex; index < allItems.length; index++) {
              const item = allItems[index];
              const itemText = item.textContent?.toLowerCase() || "";
              if (itemText.startsWith(searchChar)) {
                item.focus();
                const foundFolderId = getFolderIdFromParentElement(item);
                if (foundFolderId) {
                  setFocusableFolderId(foundFolderId);
                }
                return;
              }
            }

            // Wrap to beginning
            for (let index = 0; index < startIndex; index++) {
              const item = allItems[index];
              const itemText = item.textContent?.toLowerCase() || "";
              if (itemText.startsWith(searchChar)) {
                item.focus();
                const foundFolderId = getFolderIdFromParentElement(item);
                if (foundFolderId) {
                  setFocusableFolderId(foundFolderId);
                }
                return;
              }
            }
          }
          break;
        }
      }
    },
    [
      getAllVisibleTreeItems,
      expandedFolders,
      onFolderClick,
      onToggleExpanded,
      getFirstChild,
      focusFolder,
      findParentFolder,
      folders,
      getFolderIdFromParentElement,
    ],
  );

  // Return tabIndex for roving tabindex pattern (only one item is Tab-focusable)
  const getFocusableTabIndex = useCallback(
    (folderId: string): 0 | -1 => {
      return focusableFolderId === folderId ? 0 : -1;
    },
    [focusableFolderId],
  );

  return {
    focusableFolderId,
    setFocusableFolderId,
    handleKeyDown,
    getFocusableTabIndex,
  };
};
